// League standings, ranked by the USAV tie-break ladder.
//
// Pure and separate from the page: a standings table that is subtly wrong is
// not something anyone spots until a team disputes it at the end of a season.
//
// A match counts as played only when BOTH scores are present. A fixture with
// one score filled in is half-entered, not a 0 for the other side.
//
// USAV order, as applied here:
//   1. Match record (wins).
//   2. Two-way tie -> head-to-head; the winner of the match between them.
//   3. Three-or-more-way tie -> head-to-head among the tied teams only, then
//      set percentage, then point ratio (points won / points lost), then
//      head-to-head again if the group has been reduced to two.
//   4. Still deadlocked -> flagged, for a play-off or coin toss to settle.
//
// SET PERCENTAGE IS OMITTED, deliberately. A result is stored as one score per
// side, so a match is one "set" and sets-won equals matches-won — which is
// step 1 and cannot separate teams already tied on it.
//
// A league can now be DEFINED as multi-set (leagues.sets > 1), but nothing
// records the individual set scores yet: entering 2-1 in the boxes stores a
// two-points-to-one match, not two sets to one. Until per-set scores are
// stored, set percentage has no data to work from and this step stays out.
// When they are, it goes back in between head-to-head and point ratio, and
// pointsFor/pointsAgainst below need to become per-set totals.

/** True when a match has a usable result. */
export const isPlayed = (m) =>
  m && m.homeScore !== null && m.homeScore !== undefined &&
  m.awayScore !== null && m.awayScore !== undefined &&
  m.homeScore !== '' && m.awayScore !== '';

/** Points won / points lost — USAV's point ratio. */
export const pointRatio = (row) => {
  if (!row) return 0;
  if (!row.pointsAgainst) return row.pointsFor ? Infinity : 0;
  return row.pointsFor / row.pointsAgainst;
};

/** Point ratio for display, e.g. "1.083"; "—" before anything is played. */
export const fmtPointRatio = (row) => {
  if (!row || !row.played) return '—';
  const r = pointRatio(row);
  return r === Infinity ? '∞' : r.toFixed(3);
};

/** Blank rows for every team. */
function emptyRows(teams) {
  const rows = new Map();
  (teams || []).forEach(t => rows.set(t.id, {
    teamId: t.id, name: t.name || '',
    played: 0, wins: 0, losses: 0, draws: 0,
    pointsFor: 0, pointsAgainst: 0, diff: 0, diffPct: 0,
    deadlocked: false,
  }));
  return rows;
}

/** Accumulate matches into rows, in place. */
function tally(rows, matches) {
  (matches || []).filter(isPlayed).forEach(m => {
    const hs = Number(m.homeScore) || 0;
    const as = Number(m.awayScore) || 0;
    // A result against a team that has since been deleted still counts for the
    // team that remains, rather than vanishing from their record.
    [[rows.get(m.homeTeamId), hs, as], [rows.get(m.awayTeamId), as, hs]].forEach(([row, f, ag]) => {
      if (!row) return;
      row.played += 1;
      row.pointsFor += f;
      row.pointsAgainst += ag;
      row.diff = row.pointsFor - row.pointsAgainst;
      const total = row.pointsFor + row.pointsAgainst;
      row.diffPct = total > 0 ? row.diff / total : 0;
      if (f > ag) row.wins += 1;
      else if (f < ag) row.losses += 1;
      else row.draws += 1;
    });
  });
  return rows;
}

/**
 * Order a group of teams already level on match record.
 *
 * Returns them ranked, marking any that remain genuinely inseparable — USAV
 * sends those to a play-off or coin toss, which is not something code should
 * decide on its own.
 */
function breakTie(group, matches, depth = 0) {
  if (group.length <= 1) return group;
  const ids = new Set(group.map(r => r.teamId));

  // Head-to-head: only the matches among these teams. For two that is simply
  // who won when they met; for more it is their record against each other.
  // Recomputed rather than reused — an overall record says nothing about how a
  // team fared inside the group.
  const among = (matches || []).filter(m =>
    isPlayed(m) && ids.has(m.homeTeamId) && ids.has(m.awayTeamId));

  if (among.length && depth < 5) {
    const sub = tally(emptyRows(group.map(r => ({ id: r.teamId, name: r.name }))), among);
    const buckets = new Map();
    group.forEach(r => {
      const w = sub.get(r.teamId)?.wins || 0;
      if (!buckets.has(w)) buckets.set(w, []);
      buckets.get(w).push(r);
    });
    if (buckets.size > 1) {
      // Head-to-head separated at least some of them. Each remaining cluster
      // re-enters the ladder, so a 3-way splitting into 1 + 2 finishes with
      // head-to-head between the last two — the USAV fallback.
      return [...buckets.keys()].sort((a, b) => b - a)
        .flatMap(w => breakTie(buckets.get(w), matches, depth + 1));
    }
  }

  // Set percentage would sit here. See the note at the top of the file.

  // Point ratio, over ALL pool matches rather than only those among the tied
  // teams — that is what USAV specifies.
  const ratioBuckets = new Map();
  group.forEach(r => {
    const key = pointRatio(r).toFixed(6);
    if (!ratioBuckets.has(key)) ratioBuckets.set(key, []);
    ratioBuckets.get(key).push(r);
  });
  if (ratioBuckets.size > 1) {
    return [...ratioBuckets.keys()].sort((a, b) => Number(b) - Number(a))
      .flatMap(k => {
        const bucket = ratioBuckets.get(k);
        return bucket.length > 1 && depth < 5
          ? breakTie(bucket, matches, depth + 1)
          : bucket;
      });
  }

  // Nothing separates them. Say so rather than inventing an order.
  const stable = [...group].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true }));
  stable.forEach(r => { r.deadlocked = true; });
  return stable;
}

/**
 * Standings for a league: match record first, then the USAV ladder.
 *
 * Teams still level after every step are marked `deadlocked`, so the table can
 * show that a play-off or coin toss is needed rather than presenting an
 * arbitrary order as though it were decided.
 */
export function computeStandings(teams, matches) {
  const rows = tally(emptyRows(teams), matches);
  const byWins = new Map();
  [...rows.values()].forEach(r => {
    if (!byWins.has(r.wins)) byWins.set(r.wins, []);
    byWins.get(r.wins).push(r);
  });

  return [...byWins.keys()].sort((a, b) => b - a)
    .flatMap(w => {
      const group = byWins.get(w);
      // Teams yet to play are not "tied" in any meaningful sense.
      if (group.every(r => r.played === 0)) {
        return [...group].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
      }
      return breakTie(group, matches);
    });
}

/** Win percentage as a 0–1 number; 0 when nothing has been played. */
export const winPct = (row) => (row.played > 0 ? row.wins / row.played : 0);

/** diffPct as a signed display string, e.g. "+18.2%". */
export const fmtDiffPct = (row) => {
  const p = (row?.diffPct || 0) * 100;
  return `${p > 0 ? '+' : ''}${p.toFixed(1)}%`;
};

/**
 * Apply an admin's manual finishing order on top of the computed standings.
 *
 * End-of-season placings decide the play-off seeding, and USAV's last step is a
 * play-off or coin toss that happens off the system — so an admin has to be
 * able to record the outcome. The override is a list of team ids; anyone not
 * named keeps their computed position after those who are.
 *
 * Stored order is honoured even if it disagrees with the maths: that is the
 * point. `overridden` marks the rows so the table can show the standings are
 * no longer purely derived.
 */
export function applyStandingsOverride(rows, order) {
  if (!Array.isArray(order) || !order.length) return rows;
  const byId = new Map(rows.map(r => [r.teamId, r]));
  const seeded = [];
  order.forEach(id => {
    const r = byId.get(id);
    // Ignore ids for teams since deleted, rather than leaving a hole.
    if (r) { seeded.push({ ...r, overridden: true }); byId.delete(id); }
  });
  // Teams added after the override was saved keep their computed order, below.
  return [...seeded, ...rows.filter(r => byId.has(r.teamId))];
}

/** Seeds for the play-off, in finishing order. */
export const seedList = (rows) =>
  rows.map((r, i) => ({ seed: i + 1, teamId: r.teamId, name: r.name }));
